import { RouterRootState } from 'connected-react-router';
import React, { FC, useEffect, useState } from 'react';
import { useGlobalState } from '../../../Providers';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Description from '../../../components/description/Description';
import { useDialog } from '../../../components/dialog/DialogContext';
import { ControllerNetwork } from '../../../components/hardware/ControllerNetwork';
import { ControllerSecurity } from "../../../components/hardware/ControllerSecurity";
import DeviceActions from '../../../components/hardware/deviceActions';
import DeviceEventActions from '../../../components/hardware/deviceEventActions';
import DeviceParameters from '../../../components/hardware/deviceParameters';
import MasterControllerCard from '../../../components/hardware/MasterControllerCard';
import { MasterControllerDetails } from '../../../components/hardware/MasterControllerDetails';
import ItemContentTabTwo from '../../../components/item-content-two/item-content-tab_two';
import { Topic } from '../../../components/item-content-two/Topic';
import SimpleItemDetails from '../../../components/item-content/SimpleItemDetails';
import { toast } from "react-toastify";
import { useLocale } from '../../../contexts/localeContext';
import { TopicContextProvider } from '../../../contexts/topic/TopicContextProvider';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { BreadcrumbLink } from '../../../models/BreadcrumbLink';
import { ContentWrapper } from '../../../models/ContentWrapper';
import { Controller } from '../../../models/Controller';
import { MultiSelectOption } from '../../../models/MultiSelectOption';
import { SimpleItemProfile } from '../../../models/SimpleItemProfile';
import { TabSummary } from '../../../models/TabSummary';
import { getControllerById, getControllerNamesWithPagnation, storeController, savePassword } from '../../../services/controllerService';
import { getErrorMessage, showLoadItemError } from '../../../services/utilities/ServiceError';
import { requiredValidation } from '../../../utilities/Validation';
import { PASSWORD_STATES } from "../../../constants/system-constants";
import ChangeDevicePasswordModal from "@/components/change-password/change-device-password-modal.tsx";
import {CONTROLLER_TYPE_NUMBERS} from "@/constants/device-constants.ts";

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

const allItemsPath = '/access-control/hardware';
const itemPath = '/access-control/hardware/master-controller/';

/**
 * Change password: the only time we don't need to enter the current password is when the system generated it.
 * @returns
 */
const MasterController: FC = () => {
    const { buildInfo } = useGlobalState() ?? {};
    const { translate } = useLocale();
    const initialTabs = [
        { label: translate.byKey(TranslationKeys.details), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.network), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.security), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.eventActions), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.actions), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.parameters), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.notes), badge: 0, showInitial: true, isActive: true }
    ];
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const [itemId, setItemId] = useState(pageId);
    const [currentItem, setCurrentItem] = useState<SimpleItemProfile>();
    const [currentPasswordState, setCurrentPasswordState] = useState<number>(0);
    const [oldIpAddress, setOldIpAddress] = useState<string>("");
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs);
    const [formikContext, setFormikContext] = useState<any>();
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [enforceDefPwdChange, setEnforceDefPwdChange] = useState(false);
    const [resyncPassword, setResyncPassword] = useState(false);
    const [macAddress, setMacAddress] = useState<string>("");
    const [pendingValues, setPendingValues] = useState<any>(null);
    const dialog = useDialog();

    const reloadPage = async () => {
        try {
            let masterController;
            setOldIpAddress("");

            if (itemId) {
                masterController = await getControllerById(itemId);
                setSelectedItem({ label: masterController.name, value: masterController.id.toString() })
                setCurrentItem(masterController);
                setOldIpAddress(masterController.ipAddress);
                setCurrentPasswordState(masterController.passwordState);
                setMacAddress(masterController.macAddress);
                setEnforceDefPwdChange(false);
                
                const updatedTabs =
                        updateTabBadgeTotal(4, masterController?.totalActions,
                            updateTabBadgeTotal(5, masterController?.totalParameters, initialTabs));
                setTabs(updatedTabs);
            }
            else {
                setTabs([initialTabs[1]]);
            }
            setBreadcrumb([
                { value: '/access-control/hardware', displayName: translate.byKey(TranslationKeys.hardware) },
                { value: itemPath + `${itemId}`, displayName: `${masterController?.name ?? ''}` }
            ]);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath) });
        }
    }

    /**
     * Handle when user voluntarily requests to change password.
     * This allows us to reset the "enforce default password change" flag and not show the warning message.
     */
    const onUserRequestedChangePwd = () => {
        setEnforceDefPwdChange(false); // User is voluntarily changing password, so we don't need to enforce.
        setResyncPassword(false); // User is voluntarily changing password, so we don't need to resync.
        setIsPasswordModalOpen(true); // Open the modal
    }

    const tabContentList: ContentWrapper[] = [{
        content: <MasterControllerDetails />,
    },
    {
        content: <ControllerNetwork isPasswordModalOpen={isPasswordModalOpen} setIsPasswordModalOpen={onUserRequestedChangePwd} />,
    },
    {
        content: <ControllerSecurity reloadPage={reloadPage}/>
    },
    {
        content: <DeviceEventActions itemId={itemId} showProvidedSelectOptions={false} />,
    },
    {
        content: <DeviceActions itemId={itemId} />,
    },
    {
        content: <DeviceParameters itemId={itemId} />
    },
    {
        content: <Description title={translate.byKey(TranslationKeys.notes)} />
    }];

    const topic: Topic = {
        accessor: 'devName',
        title: translate.byKey(TranslationKeys.masterController),
        allItemsPath: allItemsPath,
        itemPath: itemPath
    }

    useEffect(() => {
        setItemId(pageId);
    }, [pageId])

    useEffect(() => {
        reloadPage();
    }, [itemId])

    const getTabContent = (index?: number) => {
        const selectedTabContent = itemId ? tabContentList[index ?? 0] : tabContentList[1];

        return selectedTabContent.content;
    };

    const updateTabBadgeTotal = (tabIndex: number, totalCount: number, currentTabs: TabSummary[]) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        return newTabs;
    }

    const checkSyncChangesToHardware = (values, currentItem) => {
        if (values.safeIp != currentItem.safeIp
            || values.staticIpAddress != currentItem.staticIpAddress
            || values.gateway != currentItem.gateway
            || values.subnetMask != currentItem.subnetMask
            || values.incomingPortFromEngine != currentItem.incomingPortFromEngine
            || values.hostname != currentItem.hostname
            || values.tlsPort != currentItem.tlsPort
            || values.tlsUrl != currentItem.tlsUrl
            || values.tlsMode != currentItem.tlsMode
            || values.outgoingPortFromEngine != currentItem.outgoingPortFromEngine) {
            return true;
        }
        return false;
    }

    const saveControllerData = (values) => {
        const newController: Controller = {
            ...currentItem,
            description: values.description,
            name: values.name,
            id: values.id,
            enabled: values.enabled,
            buildingId: values.buildingId,
            floorId: values.floorId,
            presetId: values.presetId,
            preset: values.preset,
            heartBeatReporting: values.heartBeatReporting,
            forceChanges: values.forceChanges,
            staticIpAddress: values.staticIpAddress ? !!values.staticIpAddress : false,
            safeIp: values.safeIp,
            hostname: values.hostname,
            gateway: values.gateway,
            incomingPortFromDC: values.incomingPortFromDC,
            incomingPortFromEngine: values.incomingPortFromEngine,
            outgoingPortFromEngine: values.outgoingPortFromEngine,
            subnetMask: values.subnetMask,
            updateType: values.updateType,
            ipAddress: values.ipAddress,
            wanIpAddress: values.wanIpAddress,
            tlsMode: values.tlsMode ? 1 : 0,
            tlsUrl: values.tlsUrl,
            tlsPort: values.tlsPort,
            isDefault: values.isDefault,
            certificates: [
                ...values.certificates,
                ...(values.ksCertificateToUploadId ? [{
                    ksCertificateId: Number(values.ksCertificateToUploadId),
                    ksCertificateName: "",
                    ksCertificateStatus: ""
                }] : [])
            ],
            synchroniseChangesToHardware: checkSyncChangesToHardware(values, currentItem)
        }

        setBusy(true);
        storeController(newController, false, oldIpAddress)
            .then(async (value: any) => {
                setItemId(value.id)
                history.push(`${itemPath}${value.id}`);
                toast.success(translate.byKey(TranslationKeys.recordUpdated));
                setCurrentItem(newController);
            })
            .catch(failure => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
                if (formikContext) {
                    formikContext.resetForm();
                }
            })
            .finally(() => {
                setBusy(false);
                reloadPage();
            });
    }

    const onChangePassword = (passwordValues) => {
        setBusy(true);
        savePassword(passwordValues.newPassword, (currentItem as any)?.discoveryId, passwordValues.currentPassword, resyncPassword)
            .then((response) => {
                toast?.success(translate.byKey(TranslationKeys.passwordChangedSuccessfully));
                if (pendingValues) {
                    saveControllerData(pendingValues);
                    setPendingValues(null);
                }
                setIsPasswordModalOpen(false);
                setResyncPassword(false);
                setEnforceDefPwdChange(false);
            }).catch((error) => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
            }).finally(() => {
                setIsPasswordModalOpen(false);
                setResyncPassword(false);
                setEnforceDefPwdChange(false);
                setBusy(false);
                reloadPage();
            })
    }

    const editController = async (values) => {

        // Check if current controller is using default password
        const isDefaultPassword: boolean = (currentItem as any).isDefaultPassword;
        const resyncPed: boolean = (currentItem as any).passwordState === PASSWORD_STATES.USER_DEFINED && isDefaultPassword;
        /**
         * If "Default Password Validation" is enforced, we need to check if the controller is still using the default password.
         * If it is using default password, we must block edits (except enable/disable) and prompt the user to change the password first.
        */
        if (!!buildInfo?.isEnforceDefPwdValidation) { // Only do this if the build setting is enabled
            if (isDefaultPassword || resyncPed) {
                // If only enabling/disabling, allow it.
                const isEnabledToggled = currentItem && values.enabled !== (currentItem as any).enabled;
                if (isEnabledToggled) { // TODO: this is a loop-hole. If you toggle enabled state AND change other settings, it will save without prompting.
                    saveControllerData({ ...currentItem, enabled: values.enabled });
                    return;
                }

                // Otherwise, prompt to change password first.
                setPendingValues(values);
                setEnforceDefPwdChange(isDefaultPassword && !resyncPed);
                setResyncPassword(resyncPed);
                setIsPasswordModalOpen(true);
                return;
            }
        }
        else if (resyncPed) {
            // This means that the controller needs to have the password in the db updated by the user.
            setPendingValues(values);
            setEnforceDefPwdChange(false);
            setResyncPassword(resyncPed);
            setIsPasswordModalOpen(true);
            return;
        }

        setEnforceDefPwdChange(false);
        saveControllerData(values);
    }

    return <TopicContextProvider itemId={itemId} dataMethod={getControllerNamesWithPagnation} topic={topic} selectedItem={selectedItem}>
        <ItemContentTabTwo>
            <SimpleItemDetails
                breadcrumb={breadcrumb}
                busy={busy}
                getTabContent={getTabContent}
                itemProfile={currentItem}
                onBack={() => history.push(allItemsPath)}
                saveDetails={editController}
                tabs={tabs}
                initialValues={currentItem}>
                <MasterControllerCard setFormikContext={setFormikContext} />
                <ChangeDevicePasswordModal isOpen={isPasswordModalOpen} setIsOpen={setIsPasswordModalOpen} onChangePassword={onChangePassword} hideCurrentPassword={(currentPasswordState === PASSWORD_STATES.SYSTEM_GENERATED)}
                                           enforceDefPwdChange={enforceDefPwdChange} resyncPwd={resyncPassword} macAddress={macAddress} />
            </SimpleItemDetails>
        </ItemContentTabTwo>
    </TopicContextProvider>
}

export default MasterController;
